/**
 * hooks/usePolling.js
 *
 * Hook pour exécuter une fonction à intervalle régulier, avec pause automatique
 * quand l'onglet n'est pas visible (économie de ressources serveur).
 *
 * PROBLÈME RÉSOLU (Bug #9) :
 * De nombreuses pages utilisaient `setInterval(fetchData, 5000)` sans vérifier
 * `document.hidden`. Quand l'utilisateur changeait d'onglet, 20+ pages
 * continuaient d'appeler le backend toutes les 5 sec en parallèle → charge
 * serveur inutile majeure.
 *
 * USAGE :
 *   import { usePolling } from '../hooks/usePolling';
 *
 *   const fetchData = useCallback(async () => { ... }, [deps]);
 *   usePolling(fetchData, 5000);  // pause auto si onglet masqué
 *   usePolling(fetchData, 30000);  // recommandé pour la plupart des listes
 *
 * OPTIONS :
 *   usePolling(fetchData, intervalMs, depsArray, {
 *     runOnMount: true,       // exécuter immédiatement au montage (default: true)
 *     refetchOnFocus: true,  // re-fetch quand l'onglet redevient visible (default: true)
 *   });
 */

import { useEffect, useRef } from 'react';

export function usePolling(callback, intervalMs = 30000, deps = [], options = {}) {
  const {
    runOnMount = true,
    refetchOnFocus = true,
  } = options;

  // Toujours garder la dernière version de callback dans une ref pour éviter
  // de recréer l'interval quand la callback change (notamment si elle dépend
  // d'états qui changent souvent).
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (runOnMount) {
      savedCallback.current();
    }

    const handleVisibilityChange = () => {
      if (refetchOnFocus && !document.hidden) {
        savedCallback.current();
      }
    };

    const interval = setInterval(() => {
      // ✅ Ne pas exécuter si l'onglet n'est pas visible — économie serveur
      if (!document.hidden) {
        savedCallback.current();
      }
    }, intervalMs);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
